'use client';

import { MsalProvider, useMsal } from "@azure/msal-react";
import { PublicClientApplication, BrowserAuthError } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "../config/authConfig";
import { useEffect, useState, useCallback } from 'react';
import { debug } from '../utils/logger';

// MSAL error codes that can be recovered by clearing state
const RECOVERABLE_ERROR_CODES = [
  'interaction_in_progress',
  'monitor_window_timeout',
  'popup_window_error',
  'empty_window_error'
];

const MAX_RECOVERY_ATTEMPTS = 2;

/**
 * Clears stale MSAL state from browser storage.
 * Useful for recovering from interrupted auth flows.
 */
const clearMsalState = () => {
  debug('[MSAL Recovery] Clearing stale MSAL state...');

  // Clear MSAL sessionStorage items
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('msal.') || key.includes('login') || key.includes('interaction')) {
      sessionStorage.removeItem(key);
    }
  });

  // Clear MSAL localStorage items
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('msal.') && (key.includes('interaction') || key.includes('request'))) {
      localStorage.removeItem(key);
    }
  });

  localStorage.removeItem('msal.interaction.status');
  debug('[MSAL Recovery] State cleared');
};

const LoadingSpinner = ({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4" />
    <p className="text-gray-600">{message}</p>
  </div>
);

const RecoveryNotice = ({ attempt, maxAttempts }) => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600 mx-auto mb-4" />
      <h3 className="text-yellow-800 font-medium mb-2">Recovering Authentication</h3>
      <p className="text-yellow-700 text-sm">
        A previous authentication was interrupted. Attempting automatic recovery...
      </p>
      <p className="text-yellow-600 text-xs mt-2">
        Attempt {attempt} of {maxAttempts}
      </p>
    </div>
  </div>
);

let msalInstance = null;

function AuthProvider({ children }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      try {
        if (!msalInstance) {
          debug('[AuthProvider] Creating MSAL instance');
          msalInstance = new PublicClientApplication(msalConfig);
        }

        debug('[AuthProvider] Initializing MSAL');
        await msalInstance.initialize();
        debug('[AuthProvider] MSAL initialized successfully');

        if (mounted) {
          setIsInitialized(true);
          setIsRecovering(false);
        }
      } catch (err) {
        console.error('[AuthProvider] Initialization error:', err);

        if (mounted) {
          // Check if this is a recoverable error
          const isRecoverable = err instanceof BrowserAuthError &&
            RECOVERABLE_ERROR_CODES.includes(err.errorCode);

          if (isRecoverable && recoveryAttempts < MAX_RECOVERY_ATTEMPTS) {
            debug(`[AuthProvider] Recoverable error detected, attempting recovery (${recoveryAttempts + 1}/${MAX_RECOVERY_ATTEMPTS})`);
            setIsRecovering(true);
            setRecoveryAttempts(prev => prev + 1);

            // Clear stale state and reset instance
            clearMsalState();
            msalInstance = null;

            // Retry after a short delay
            setTimeout(() => {
              if (mounted) {
                initAuth();
              }
            }, 500);
          } else {
            setError(err);
            setIsRecovering(false);
          }
        }
      }
    }

    initAuth();

    return () => {
      mounted = false;
    };
  }, [recoveryAttempts]);

  if (isRecovering) {
    return <RecoveryNotice attempt={recoveryAttempts} maxAttempts={MAX_RECOVERY_ATTEMPTS} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 p-4 text-center">
          <p>Authentication initialization failed</p>
          <p className="text-sm">{error.message}</p>
          <button
            onClick={() => {
              clearMsalState();
              window.location.reload();
            }}
            className="mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return <LoadingSpinner message="Initializing authentication..." />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      {children}
    </MsalProvider>
  );
}

function ProtectedComponent({ children }) {
  const { instance } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isHandlingRedirect, setIsHandlingRedirect] = useState(true);
  const [recoveryAttempts, setRecoveryAttempts] = useState(0);
  const [isRecovering, setIsRecovering] = useState(false);

  const attemptRecovery = useCallback(async (error) => {
    if (recoveryAttempts >= MAX_RECOVERY_ATTEMPTS) {
      debug('[Auth Recovery] Max recovery attempts reached');
      setAuthError(error);
      setIsRecovering(false);
      return false;
    }

    debug(`[Auth Recovery] Attempting recovery (${recoveryAttempts + 1}/${MAX_RECOVERY_ATTEMPTS}) for error: ${error.errorCode}`);
    setIsRecovering(true);
    setRecoveryAttempts(prev => prev + 1);

    // Clear stale MSAL state
    clearMsalState();

    // Wait a moment for state to clear
    await new Promise(resolve => setTimeout(resolve, 500));

    return true;
  }, [recoveryAttempts]);

  useEffect(() => {
    let isSubscribed = true;

    const checkAuth = async () => {
      try {
        debug('[Auth Check] Starting auth check...');

        // Set handling redirect state
        setIsHandlingRedirect(true);

        // Handle any pending redirect first and wait for it to complete
        const result = await instance.handleRedirectPromise();
        debug('[Auth Check] Redirect result:', result ? 'Success' : 'No redirect present');

        // Only proceed if component is still mounted
        if (!isSubscribed) return;

        // Update handling redirect state
        setIsHandlingRedirect(false);
        setIsRecovering(false);

        const currentAccounts = instance.getAllAccounts();
        debug('[Auth Check] Current accounts:', currentAccounts.length);

        if (currentAccounts.length === 0 && !result) {
          debug('[Auth Check] No accounts found, initiating login...');
          // Only initiate login if we're not already handling a redirect
          const loginConfig = {
            ...loginRequest,
            prompt: 'select_account'
          };
          await instance.loginRedirect(loginConfig);
        } else if (currentAccounts.length > 0) {
          debug('[Auth Check] Account found, verifying...');
          const account = currentAccounts[0];
          try {
            await instance.acquireTokenSilent({
              ...loginRequest,
              account
            });
            if (isSubscribed) {
              setIsAuthenticated(true);
            }
          } catch (error) {
            if (error.name === "InteractionRequiredAuthError" && !isHandlingRedirect) {
              await instance.acquireTokenRedirect({
                ...loginRequest,
                account
              });
            } else {
              throw error;
            }
          }
        }
      } catch (error) {
        console.error('[Auth Check] Authentication error:', error);

        if (!isSubscribed) return;

        if (error instanceof BrowserAuthError) {
          console.error('[Auth Check] Browser auth error details:', {
            code: error.errorCode,
            message: error.message
          });

          // Handle user cancellation silently
          if (error.errorCode === 'user_cancelled') {
            debug('[Auth Check] User cancelled authentication');
            return;
          }

          // Check if error is recoverable
          if (RECOVERABLE_ERROR_CODES.includes(error.errorCode)) {
            const shouldRetry = await attemptRecovery(error);
            if (shouldRetry) {
              // Trigger re-run of checkAuth by updating state
              return;
            }
          }

          setAuthError(error);
        }
      } finally {
        if (isSubscribed) {
          setIsHandlingRedirect(false);
        }
      }
    };

    checkAuth();

    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, recoveryAttempts, attemptRecovery]);

  if (isRecovering) {
    return <RecoveryNotice attempt={recoveryAttempts} maxAttempts={MAX_RECOVERY_ATTEMPTS} />;
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 p-4 text-center">
          <p>Authentication failed</p>
          <p className="text-sm">{authError.message}</p>
          <button
            onClick={() => {
              clearMsalState();
              window.location.reload();
            }}
            className="mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isHandlingRedirect || !isAuthenticated) {
    return <LoadingSpinner message={isHandlingRedirect ? "Handling redirect..." : "Authenticating..."} />;
  }

  return children;
}

export { AuthProvider, ProtectedComponent, clearMsalState };
